import React, { useState, useEffect } from 'react';
import { Route, Leaf, Gift, Share, PlusSquare, X } from 'lucide-react';
import { useI18n } from '@/i18n/I18nContext';

const InstallPrompt = () => {
  const { t } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if dismissed previously
    const dismissed = localStorage.getItem('colygon_pwa_dismissed');
    if (dismissed) {
      return;
    }

    // iOS detection
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isStandaloneMode = ('standalone' in window.navigator) && window.navigator.standalone;

    setIsIOS(isIosDevice);
    setIsStandalone(isStandaloneMode);

    if (isIosDevice && !isStandaloneMode) {
      // Small delay to ensure smooth rendering before showing
      setTimeout(() => setIsVisible(true), 2000);
    }

    // PWA BeforeInstallPrompt (Android / Chrome Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setIsVisible(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    
    setIsVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('colygon_pwa_dismissed', 'true');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300">
      <div className="w-full max-w-md bg-white rounded-t-3xl shadow-2xl p-6 relative animate-in slide-in-from-bottom-full duration-300">
        
        {/* Close Button */}
        <button 
          onClick={handleDismiss}
          className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-6 mt-2">
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">
            {t("install.title")}
          </h2>
          <p className="text-sm text-slate-500 mt-1 font-medium">
            {t("install.subtitle")}
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Route className="w-5 h-5" />
            </div>
            <div className="text-sm font-semibold text-slate-700">{t("install.feat_checkin")}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <Leaf className="w-5 h-5" />
            </div>
            <div className="text-sm font-semibold text-slate-700">{t("install.feat_co2")}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5" />
            </div>
            <div className="text-sm font-semibold text-slate-700">{t("install.feat_payroll")}</div>
          </div>
        </div>

        {isIOS && !isStandalone ? (
          <div className="bg-slate-50 rounded-2xl p-4 text-center border border-slate-100">
            <p className="text-sm text-slate-600 font-medium">
              {t("install.ios_1")} <Share className="inline-block w-4 h-4 mx-1" /> {t("install.ios_share")} {t("install.ios_2")} <span className="font-bold inline-flex items-center gap-1 text-slate-800"><PlusSquare className="w-3 h-3"/> {t("install.ios_add")}</span>.
            </p>
          </div>
        ) : (
          <button
            onClick={handleInstallClick}
            className="w-full bg-colygon hover:bg-colygon/90 text-white font-bold py-3.5 px-4 rounded-2xl transition-all shadow-lg shadow-colygon/20"
          >
            {t("install.install_btn")}
          </button>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
